<template>
  <div class="mobile-bar-root">
    <!-- In normal flow, so the page ends above the bar without any global
         body padding. Requires MobileBar to be the last child of the page. -->
    <div class="mobile-bar-spacer" aria-hidden="true"></div>

    <nav class="mobile-bar glass" :aria-label="$t('mobile.nav')">
      <ul class="mb-links">
        <li v-for="s in sections" :key="s.id" class="mb-item">
          <a
            class="mb-link"
            :class="{ active: active === s.id }"
            :href="`#${s.id}`"
            :aria-current="active === s.id ? 'location' : null"
            @click.prevent="scrollTo(s.id)"
          >
            {{ $t(s.key) }}
          </a>
        </li>
      </ul>

      <div class="mb-lang" role="group" :aria-label="$t('mobile.language')">
        <button
          type="button"
          class="mb-lang-btn"
          :class="{ active: currentLocale === 'en' }"
          :aria-pressed="currentLocale === 'en'"
          :aria-label="$t('mobile.english')"
          @click="switchLanguage('en')"
        >
          EN
        </button>
        <button
          type="button"
          class="mb-lang-btn"
          :class="{ active: currentLocale === 'nl' }"
          :aria-pressed="currentLocale === 'nl'"
          :aria-label="$t('mobile.dutch')"
          @click="switchLanguage('nl')"
        >
          NL
        </button>
      </div>
    </nav>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useIsNarrow } from "@/composables/useMediaQuery";

/* The four anchors the page actually has. "hero" is tracked but not linked —
   at the top of the page no tab is current, exactly as in AppNavigation. */
const ORDER = ["hero", "work", "about", "contact"];

export default {
  name: "MobileBar",
  setup() {
    const { locale } = useI18n();
    const isNarrow = useIsNarrow();
    const active = ref("hero");

    const sections = [
      { id: "work", key: "nav.work" },
      { id: "about", key: "nav.about" },
      { id: "contact", key: "nav.contact" },
    ];

    const currentLocale = computed(() => locale.value);

    // Section offsets are cached, so the scroll frame only reads scrollY —
    // the same discipline useDepth uses. Re-measured on resize and on locale
    // change, because NL/EN copy changes section heights.
    let tops = [];
    let frame = null;
    let resizeTimer = null;
    let bound = false;
    let onScroll = null;
    let onResize = null;
    let ro = null;

    // Document-relative, matching scrollTo's own coordinate system. offsetTop
    // would be relative to the nearest positioned ancestor — App.vue makes
    // <main> position: relative, so the two would silently disagree the moment
    // anything sits above main in flow. Never called from inside a rAF (§8).
    const measure = () => {
      const y = window.scrollY;
      tops = ORDER.map((id) => {
        const el = document.getElementById(id);
        return el ? el.getBoundingClientRect().top + y : Infinity;
      });
    };

    const read = () => {
      frame = null;
      const mid = window.scrollY + window.innerHeight * 0.35;
      let current = ORDER[0];
      for (let i = ORDER.length - 1; i >= 0; i--) {
        if (tops[i] <= mid) {
          current = ORDER[i];
          break;
        }
      }
      active.value = current;
    };

    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(read);
    };

    const bind = () => {
      if (bound) return;
      bound = true;
      measure();
      read();
      onScroll = schedule;
      onResize = () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          measure();
          schedule();
        }, 150);
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onResize);
      // Resize alone is not enough: opening a ledger dossier, a lazy mark
      // landing or the reveal settling all move #about and #contact down
      // without a resize event, and the cached tops would go stale for the
      // rest of the session. §8 names ResizeObserver as a legal re-measure
      // trigger; it shares the same 150 ms debounce and never touches a rAF.
      if ("ResizeObserver" in window) {
        ro = new ResizeObserver(onResize);
        ro.observe(document.body);
      }
    };

    const unbind = () => {
      if (!bound) return;
      bound = false;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (ro) {
        ro.disconnect();
        ro = null;
      }
      clearTimeout(resizeTimer);
      if (frame !== null) cancelAnimationFrame(frame);
      frame = null;
    };

    const switchLanguage = (lang) => {
      locale.value = lang;
      try {
        localStorage.setItem("locale", lang);
      } catch (e) {
        // ignore
      }
    };

    const scrollTo = (id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 60;
      const reduced = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      window.scrollTo({ top, behavior: reduced ? "auto" : "smooth" });
    };

    // The bar is CSS-hidden above 720px; above it the listeners do not exist.
    onMounted(() => {
      if (isNarrow.value) bind();
    });
    watch(isNarrow, (narrow) => (narrow ? bind() : unbind()));
    watch(locale, () => {
      if (!bound) return;
      nextTick(() => {
        measure();
        schedule();
      });
    });

    onBeforeUnmount(unbind);

    return { sections, active, currentLocale, switchLanguage, scrollTo };
  },
};
</script>

<style scoped>
.mobile-bar-root {
  display: none;
}

.mobile-bar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 90;
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: var(--s-2);
  height: calc(56px + env(safe-area-inset-bottom, 0px));
  padding: 0 var(--edge) env(safe-area-inset-bottom, 0px);
  border-top: var(--hair-w) solid var(--hairline);
}

/* Matches the bar's own height, so the colophon never sits under the glass. */
.mobile-bar-spacer {
  height: calc(56px + env(safe-area-inset-bottom, 0px));
}

.mb-links {
  display: flex;
  align-items: stretch;
  list-style: none;
  margin: 0;
  padding: 0;
}
.mb-item {
  display: flex;
}

.mb-link {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  padding: 0 var(--s-3);
  font-size: var(--t-sm);
  font-weight: 600;
  letter-spacing: var(--ls-title);
  color: var(--grey);
  transition: color var(--d-2) var(--ease-inout);
}
.mb-link.active {
  color: var(--ink);
}
.mb-link::after {
  content: "";
  position: absolute;
  left: var(--s-3);
  right: var(--s-3);
  bottom: 12px;
  height: 2px;
  border-radius: 1px;
  background: var(--accent);
  transform: scaleX(0);
  transition: transform var(--d-2) var(--ease-inout);
}
.mb-link.active::after {
  transform: scaleX(1);
}

.mb-lang {
  display: inline-flex;
  align-self: center;
  border: var(--hair-w) solid var(--hairline-strong);
  border-radius: var(--r-pill);
}
.mb-lang-btn {
  min-width: 44px;
  min-height: 44px;
  padding: 0 var(--s-2);
  border: 0;
  border-radius: var(--r-pill);
  background: transparent;
  font-family: var(--font-measure);
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.06em;
  color: var(--grey);
  cursor: pointer;
  transition: color var(--d-1) linear,
    background var(--d-2) var(--ease-inout);
}
.mb-lang-btn.active {
  background: var(--accent);
  color: #fff;
}

/* Inset rings: an outside offset would be clipped by the bar's top edge. */
.mb-link:focus-visible,
.mb-lang-btn:focus-visible {
  outline-offset: -2px;
}

@media (max-width: 720px) {
  .mobile-bar-root {
    display: block;
  }
}
</style>
